import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Megaphone, Plus, Trash2, Pin } from "lucide-react";

export default function Announcements() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", pinned: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/announcements");
    setItems(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/announcements", form);
      toast.success("Announcement posted");
      setForm({ title: "", body: "", pinned: false });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this announcement?")) return;
    await api.delete(`/announcements/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="announcements-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <Megaphone className="w-4 h-4" /> Community news
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Announcements</h1>
        </div>
        {isBoard && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full h-11" data-testid="new-announcement-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New announcement
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Post an announcement</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="announcement-form">
                <div>
                  <Label htmlFor="a-title">Title</Label>
                  <Input id="a-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 h-11" data-testid="announcement-title-input" />
                </div>
                <div>
                  <Label htmlFor="a-body">Message</Label>
                  <Textarea id="a-body" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1.5 min-h-[120px]" data-testid="announcement-body-input" />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={form.pinned} onCheckedChange={(v) => setForm({ ...form, pinned: !!v })} data-testid="announcement-pinned-check" />
                  Pin to top
                </label>
                <DialogFooter>
                  <Button type="submit" disabled={saving} className="rounded-full" data-testid="announcement-submit-btn">
                    {saving ? "Posting..." : "Post announcement"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="announcements-empty">
            No announcements yet.
          </div>
        )}
        {items.map((a) => (
          <div key={a.id} className="hoa-card p-6" data-testid={`announcement-${a.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {a.pinned && (
                    <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1">
                      <Pin className="w-3 h-3" /> Pinned
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    by {a.author_name} · {new Date(a.created_at).toLocaleString()}
                  </span>
                </div>
                <h3 className="font-semibold text-lg text-foreground">{a.title}</h3>
                <p className="text-foreground/80 mt-2 whitespace-pre-line leading-relaxed">{a.body}</p>
              </div>
              {isBoard && (
                <button
                  className="text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => del(a.id)}
                  data-testid={`delete-announcement-${a.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
